import React, { useEffect, useMemo, useRef } from 'react';

// Stable empty array constant — avoids creating a new [] reference on every render,
// which would cause the useMemo for transformedData to recompute unnecessarily.
const EMPTY_FAVORITE_PLANS: string[] = [];
import { View, StyleSheet, Text, Animated, Platform } from 'react-native';
import DataTable from '../../../components/DataTable';
import { useTheme } from '../../../theme';
import FontFamily from '../../../constants/fonts';
import { ActionPlanItem, ActionPlanTableProps } from '../types';
import { getSourceDisplayText, getTypeDisplayText, getPriorityDisplayText } from '../utils/formOptions';
import { formatNameFromEmail } from '../utils/actionPlanTransformers';

// Utility function to format date consistently as "Jan 22, 2026"
// Parse UTC date string WITHOUT timezone conversion
const formatDateToDDMMYYYY = (dateString?: string): string => {
  if (!dateString) return '';
  
  try {
    // Parse ISO string directly without timezone conversion
    const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const year = isoMatch[1];
      const month = parseInt(isoMatch[2], 10);
      const day = parseInt(isoMatch[3], 10);
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      return `${monthNames[month - 1]} ${day}, ${year}`;
    }
    
    return dateString;
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString;
  }
};

// Column configuration that matches the Figma design
const columns = [
  { key: 'title', title: 'Title', flex: 3 },
  { key: 'assignedUser', title: 'Assigned User', flex: 2 },
  { key: 'status', title: 'Status', flex: 1.5 },
  { key: 'priority', title: 'Priority', flex: 1.5 },
  { key: 'targetDate', title: 'Target Date', flex: 2 },
];

// Skeleton component for table row loading state
const TableRowSkeleton: React.FC<{ isDark?: boolean; skeletonCellBg?: string }> = ({ isDark, skeletonCellBg }) => {
  const opacity = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(opacity, {
          toValue: 0.3,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [opacity]);

  return (
    <View style={[styles.skeletonRow, isDark && { borderColor: '#404040' }]}>
      <Animated.View style={[styles.skeletonCell, styles.skeletonCellLarge, isDark && { backgroundColor: skeletonCellBg }, { opacity }]} />
      <Animated.View style={[styles.skeletonCell, styles.skeletonCellMedium, isDark && { backgroundColor: skeletonCellBg }, { opacity }]} />
      <Animated.View style={[styles.skeletonCell, styles.skeletonCellSmall, isDark && { backgroundColor: skeletonCellBg }, { opacity }]} />
      <Animated.View style={[styles.skeletonCell, styles.skeletonCellSmall, isDark && { backgroundColor: skeletonCellBg }, { opacity }]} />
      <Animated.View style={[styles.skeletonCell, styles.skeletonCellMedium, isDark && { backgroundColor: skeletonCellBg }, { opacity }]} />
    </View>
  );
};

const ActionPlanTable: React.FC<ActionPlanTableProps> = ({ 
  data, 
  isLoading, 
  error,
  onEditPress,
  onViewDetailsPress,
  onDeleteSuccess,
  onStatusChange,
  onTransformedData,
  // Pagination props
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  // Favorite props
  showFavoriteIcon = false,
  onFavoriteToggle,
  favoriteActionPlans = EMPTY_FAVORITE_PLANS,
  hideActionMenu = false,
  alwaysShowPagination = false
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  // Handle edit button press - convert row data back to ActionPlanItem
  const handleEditPress = (rowData: any) => {
    if (onEditPress) {
      // Find the original ActionPlanItem by ID
      const originalItem = data.find(item => String(item.ID) === rowData.id);
      if (originalItem) {
        onEditPress(originalItem);
      }
    }
  };

  // Handle view details button press - convert row data back to ActionPlanItem
  const handleViewDetailsPress = (rowData: any) => {
    if (onViewDetailsPress) {
      // Find the original ActionPlanItem by ID
      const originalItem = data.find(item => String(item.ID) === rowData.id);
      if (originalItem) {
        onViewDetailsPress(originalItem);
      }
    } else if (onEditPress) {
      // Fallback: use onEditPress if onViewDetailsPress not provided
      const originalItem = data.find(item => String(item.ID) === rowData.id);
      if (originalItem) {
        onEditPress(originalItem);
      }
    }
  };

  // Handle successful delete - convert ID back to find and remove from data
  const handleDeleteSuccess = (deletedItemId: string) => {
    if (onDeleteSuccess) {
      onDeleteSuccess(deletedItemId);
    }
  };

  // Transform API data to match DataTable expected format
  // Must be before any conditional returns (Rules of Hooks)
  const transformedData = useMemo<any[]>(() => {
    return (data && data.length > 0) ? data.map((item) => ({
      id: String(item.ID),
      title: item.TITLE || '',
      assignedUser: formatNameFromEmail(item.ASSIGNEE_EMAIL, item.ASSIGNEE_GPID),
      // Show ASSIGNMENT_STATUS value if it's "declined", otherwise show STATUS
      status: item.ASSIGNMENT_STATUS?.toLowerCase() === 'declined' ? (item.ASSIGNMENT_STATUS || '') : (item.STATUS || ''),
      priority: getPriorityDisplayText(item.PRIORITY) || '',
      targetDate: formatDateToDDMMYYYY(item.TARGET_DATE),
      targetDateRaw: item.TARGET_DATE, // Keep raw date for sorting
      badgeText: item.STATUS === 'New' ? 'NEW' : undefined,
      isTemp: false,
      isFavorite: favoriteActionPlans.includes(String(item.ID)),
      // Include raw data for role-based status access control and delete permissions
      rawData: {
        ASSIGNEE_EMAIL: item.ASSIGNEE_EMAIL,
        OWNER_EMAIL: item.OWNER_EMAIL,
        LOCATION: item.LOCATION, // Action plan location for Site Leader access check
        OWNER_ROLE: item.OWNER_ROLE, // Owner's role for delete permission check
        ASSIGNMENT_STATUS: item.ASSIGNMENT_STATUS, // Check if assignment is declined
      },
      expansionData: {
        actionPlanOwner: formatNameFromEmail(item.OWNER_EMAIL, item.OWNER_GPID),
        type: getTypeDisplayText(item.TYPE) || '',
        startDate: formatDateToDDMMYYYY(item.START_DATE),
        source: getSourceDisplayText(item.SOURCE) || '',
        frontlineEmployee: item.INVOLVED_EMPLOYEE_NAMES || '',
        actionPlan: item.ACTION_PLAN_DESCRIPTION || '',
      },
    })) : [];
  }, [data, favoriteActionPlans]);

  useEffect(() => {
    onTransformedData?.(transformedData);
  }, [onTransformedData, transformedData]);

  // Show loading state
  if (isLoading) {
    return (
      <View style={[styles.container, isDark && { backgroundColor: colors.background.page }]}>
        <View style={[styles.tableHeader, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
          {columns.map((col) => (
            <View key={col.key} style={[styles.headerCell, { flex: col.flex }]}>
              <Text style={[styles.headerText, isDark && { color: colors.text.primary }]}>{col.title}</Text>
            </View>
          ))}
        </View>
        <View style={[styles.skeletonContainer, isDark && { backgroundColor: colors.background.surface }]}>
          {[...Array(5)].map((_, index) => (
            <TableRowSkeleton key={`skeleton-${index}`} isDark={isDark} skeletonCellBg={colors.border.default} />
          ))}
        </View>
      </View>
    );
  }

  // Show error state
  if (error) {
    return (
      <View style={[styles.container, isDark && { backgroundColor: colors.background.page }]}>
        <View style={styles.messageContainer}>
          <Text style={[styles.errorText, isDark && { color: colors.status.error }]}>Error: {error}</Text>
        </View>
      </View>
    );
  }

  // Show empty state (but still render DataTable with pagination if alwaysShowPagination is true)
  if (!data || data.length === 0) {
    if (!alwaysShowPagination) {
      return (
        <View style={[styles.container, isDark && { backgroundColor: colors.background.page }]}>
          <View style={styles.messageContainer}>
            <Text style={[styles.emptyText, isDark && { color: colors.text.secondary }]}>No action plans found</Text>
          </View>
        </View>
      );
    }
    // If alwaysShowPagination is true, continue to render DataTable with empty data
    // so pagination controls remain visible
  }

  return (
    <View style={styles.container}>
      <DataTable 
        columns={columns} 
        data={transformedData as any} 
        onEditPress={handleEditPress}
        onViewDetailsPress={handleViewDetailsPress}
        onDeleteSuccess={handleDeleteSuccess}
        onStatusChange={onStatusChange}
        currentPage={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showFavoriteIcon={showFavoriteIcon}
        onFavoriteToggle={onFavoriteToggle}
        hideActionMenu={hideActionMenu}
        alwaysShowPagination={alwaysShowPagination}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    //backgroundColor: '#FFFFFF',
    width: '100%',
    paddingHorizontal: 0,
  },
  messageContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#005CBC',
    fontFamily: FontFamily.regular,
  },
  errorText: {
    fontSize: 16,
    color: '#dc3545',
    fontFamily: FontFamily.regular,
  },
  emptyText: {
    fontSize: 16,
    color: '#6c757d',
    fontFamily: FontFamily.regular,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F7FAFC',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#E0E0E0',
  },
  headerCell: {
    justifyContent: 'center',
  },
  headerText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1C',
    fontFamily: FontFamily.medium,
  },
  skeletonContainer: {
    backgroundColor: '#FFFFFF',
  },
  skeletonRow: {
    flexDirection: 'row',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderColor: '#E0E0E0',
    gap: 12,
  },
  skeletonCell: {
    height: 16,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
  },
  skeletonCellLarge: {
    flex: 3,
  },
  skeletonCellMedium: {
    flex: 2,
  },
  skeletonCellSmall: {
    flex: 1.5,
  },
});

export default ActionPlanTable;

